/** @jsx jsx */
import { css, jsx, Theme } from '@emotion/react'
import { AxiosError } from 'axios'
import React, { useState } from 'react'
import { Button } from '@vendorflow/components'
import {
  InsertDriveFile as InsertDriveFileIcon,
  Download as DownloadIcon,
  Downloading as DownloadingIcon,
} from '@mui/icons-material'
import { useIsOnMobile } from 'pages/WorkspacePage/Workspace.service'
import { downloadAssetToMachine, downloadFile } from 'services/Files.services'
import { useNotificationsContext } from 'features/notifications/NotificationsProvider'

interface Props {
  href: string
  filename: string
}

export default function DocumentDownloadButton({ href, filename }: Props) {
  const { enqueueSnackbarError } = useNotificationsContext()
  const [isHover, setIsHover] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isOnMobile = useIsOnMobile()

  async function handleOnClick() {
    setIsLoading(true)
    try {
      const file = await downloadFile(href)
      downloadAssetToMachine(file, filename)
    } catch (e) {
      const status = (e as AxiosError)?.response?.status
      if (status === 403 || status === 401) {
        enqueueSnackbarError({ message: `${filename} has expired and is no longer accessible.` })
      } else {
        enqueueSnackbarError({ message: `Unable to download ${filename} because of an unexpected error.` })
      }
    } finally {
      setIsLoading(false)
    }
  }

  function renderIcon() {
    if (isLoading) {
      return (
        <DownloadingIcon
          css={(theme: Theme) => css`
            color: ${theme.custom.palette.grey[33]};
          `}
        />
      )
    }
    return isHover || isOnMobile ? (
      <DownloadIcon
        css={(theme: Theme) => css`
          color: ${theme.custom.palette.grey[33]};
        `}
      />
    ) : (
      <InsertDriveFileIcon
        css={(theme: Theme) => css`
          color: ${theme.custom.palette.grey[33]};
        `}
      />
    )
  }

  return (
    <div>
      <Button
        data-testid="attachment-download-button"
        css={css`
          padding: 0.5rem;
          display: flex;
          align-items: center;
          width: fit-content;
          text-transform: none;
          background-color: inherit;

          &:hover {
            background-color: inherit;
          }
        `}
        title="Download"
        onClick={async () => await handleOnClick()}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        disabled={isLoading}
      >
        {renderIcon()}
        <div
          css={css`
            color: black;
          `}
        >
          {filename}
        </div>
      </Button>
    </div>
  )
}
